import React from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField
} from '@material-ui/core'

import {
    BodyTableCell,
    ContainerDiv,
    FlexBaselineDiv,
    FlexColumnDiv,
    FlexSpaceBetweenDiv,
    formatMainDescription,
    HeaderTableCell,
    OverflowXDiv
} from '../components/components'
import { DateTimeService } from '../utils/dateTimeService'
import { TagService } from './tagService'
import { SortHeaderCell } from '../components/sortHeaderCell'
import { FilterDatePicker } from '../components/filterDatePicker'
import { FilterService } from '../utils/filterService'
import { DefaultCollection } from '../collection/defaultCollection'
import { StringService } from '../utils/stringService'
import { TableFooterWithPaginator } from '../components/tableFooterWithPaginator'
import { withSnackbar } from 'notistack'
import { PrimaryButton } from '../components/components'
import styled from 'styled-components'
import { Tag as TagIcon, SlidersHorizontal } from 'lucide-react'

// Visual Universo MOB: banner navy + cards.
const PageWrap = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #f1f5f9;
    min-height: 100%;
`;
const Banner = styled.div`
    background: linear-gradient(135deg, #071a2c 0%, #0d3350 100%);
    color: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    box-shadow: 0 6px 18px rgba(7, 26, 44, 0.25);
`;
const Card = styled.div`
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
    overflow: hidden;
`;
const CardHead = styled.div`
    padding: 14px 20px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #0f172a;
    font-size: 0.95rem;
`;
const CardBody = styled.div`
    padding: 16px 20px;
`;
const Chip = styled.span`
    background: rgba(28, 165, 217, 0.18);
    color: #8fd6f2;
    border: 1px solid rgba(28, 165, 217, 0.35);
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
`;
const CountBadge = styled.span`
    background: #eff6ff;
    color: #1CA5D9;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 0.75rem;
    font-weight: 700;
    margin-left: 6px;
`;

const getAdjustedDate = (isoDate) => {
    if (!isoDate) return isoDate;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return isoDate;
    date.setHours(date.getHours() - 3);
    return date.toISOString();
};

class TagsPage extends React.Component {
    state = {
        filterQuery: '',
        filterIsDvExclusive: '',
        filterEnabled: '',
        filterUpdateFrom: null,
        filterUpdateUpTo: null,
        collection: DefaultCollection.ofItemsAndPredicates([], []),
        exporting: false,
        exportingSelected: false,
        selectedSerialNumbers: new Set()
    }

    buildPredicates = (items, filterValues) => {
        const { filterQuery, filterIsDvExclusive, filterEnabled, filterUpdateFrom, filterUpdateUpTo } = filterValues;

        return [
            FilterService.containsQuery(filterQuery, items, ['serialNumber', 'equipmentExtraLabel', 'equipmentExtraCode', 'productName']),
            FilterService.isSameString('productIsDvExclusive', filterIsDvExclusive),
            FilterService.isSameString('enabled', filterEnabled),
            FilterService.isFromValid('updateDate', filterUpdateFrom),
            FilterService.isUpToValid('updateDate', filterUpdateUpTo)
        ]
    }

    componentDidMount() {
        TagService.list().then(tags => this.setState(state => ({ collection: DefaultCollection.ofItemsAndPredicates(tags, this.buildPredicates(tags, state)) })));
    }

    updateCollection = updater => this.setState(state => ({ collection: updater(state.collection, state) }));

    updateWithUpdatedItem = updatedTag => (collection, state) => DefaultCollection.withUpdatedItem(collection, 'serialNumber', updatedTag, this.buildPredicates(collection.items, state));
    notify = message => () => this.props.enqueueSnackbar(message);

    toggleEnabled = (tag, toggle) => TagService.toggleTag(tag.serialNumber, toggle)
        .then(() => this.updateCollection(this.updateWithUpdatedItem({ ...tag, enabled: toggle })))
        .then(this.notify(`Tag ${tag.serialNumber} ${toggle ? this.props.currentLanguage.activated : this.props.currentLanguage.disabled}`));

    handleFilterChange = newFilterState => {
        this.setState(newFilterState);

        return this.updateCollection((collection, state) => DefaultCollection.withItems(collection, collection.items, this.buildPredicates(collection.items, state)));
    };
    handleSort = field => this.updateCollection(collection => DefaultCollection.withNextSorter(collection, field));

    exportActiveTags = () => {
        this.setState({ exporting: true });
        
        TagService.exportActiveTags()
            .then(() => {
                this.props.enqueueSnackbar('PDF exportado com sucesso!');
            })
            .catch(error => {
                console.error('Erro ao exportar PDF:', error);
                this.props.enqueueSnackbar('Erro ao exportar PDF', { variant: 'error' });
            })
            .finally(() => {
                this.setState({ exporting: false });
            });
    }

    isSelected = tag => this.state.selectedSerialNumbers.has(tag.serialNumber);

    toggleSelected = (tags, selected) => this.setState(state => {
        const selectedSerialNumbers = new Set(state.selectedSerialNumbers);
        tags.forEach(tag => selected ? selectedSerialNumbers.add(tag.serialNumber) : selectedSerialNumbers.delete(tag.serialNumber));

        return { selectedSerialNumbers };
    });

    clearSelection = () => this.setState({ selectedSerialNumbers: new Set() });

    // Apenas tags selecionadas que continuam na listagem filtrada, na ordem atual do grid.
    listSelectedTags = () => this.state.collection.sorter.sortedItems.filter(this.isSelected);

    exportSelectedTags = () => {
        const serialNumbers = this.listSelectedTags().map(tag => tag.serialNumber);

        if (serialNumbers.length === 0) {
            this.props.enqueueSnackbar('Selecione ao menos uma tag para exportar.', { variant: 'info' });
            return;
        }

        this.setState({ exportingSelected: true });

        TagService.exportSelectedTags(serialNumbers)
            .then(() => {
                this.props.enqueueSnackbar('PDF exportado com sucesso!');
            })
            .catch(error => {
                console.error('Erro ao exportar PDF:', error);
                this.props.enqueueSnackbar('Erro ao exportar PDF', { variant: 'error' });
            })
            .finally(() => {
                this.setState({ exportingSelected: false });
            });
    }

    clearFilters = (event) => {
        event.preventDefault();

        this.handleFilterChange({
            filterQuery: '',
            filterIsDvExclusive: '',
            filterEnabled: '',
            filterUpdateFrom: null,
            filterUpdateUpTo: null,
            collection: DefaultCollection.ofItemsAndPredicates([], [])
        })

        this.componentDidMount()
    }

    render() {
        const { allowTagToggle, currentLanguage, loggedUser } = this.props
        const { collection, filterQuery, filterIsDvExclusive, filterUpdateFrom, filterUpdateUpTo, filterEnabled, exporting, exportingSelected } = this.state;
        const pageItems = collection.paginator.pagedItems
        const listedItems = collection.sorter.sortedItems
        const selectedCount = this.listSelectedTags().length
        const pageSelectedCount = pageItems.filter(this.isSelected).length

        const fieldLabelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#334155', marginBottom: 4 };
        const dateCardStyle = { border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, background: '#fff' };
        const dateCardTitleStyle = { fontWeight: 600, fontSize: '0.9rem', color: '#0f172a', marginBottom: 12 };

        return (
            <PageWrap>
                <Banner>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <TagIcon size={26} />
                        <div>
                            <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{currentLanguage.tags}</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.75 }}>{currentLanguage.list_tag_table_description}</div>
                        </div>
                    </div>
                    <Chip>Tags utilizadas: {loggedUser.active_tags_qty}</Chip>
                </Banner>

                <Card>
                    <CardHead><SlidersHorizontal size={18} color="#1CA5D9" /> {currentLanguage.search}</CardHead>
                    <CardBody>
                    {/* Buscar */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={fieldLabelStyle}>{currentLanguage.search}</div>
                        <TextField
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            helperText={currentLanguage.equipment_serial + ", " + currentLanguage.description + ", " + currentLanguage.or + " " + currentLanguage.name}
                            value={filterQuery}
                            onChange={event => this.handleFilterChange({ filterQuery: StringService.normalize(event.target.value) })}
                        />
                    </div>

                    {/* Dropdowns */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                        <div>
                            <div style={fieldLabelStyle}>{currentLanguage.product_exclusive}</div>
                            <FormControl fullWidth margin="dense">
                                <Select variant="outlined" displayEmpty value={filterIsDvExclusive} onChange={event => this.handleFilterChange({ filterIsDvExclusive: event.target.value })} inputProps={{ id: 'isDvExclusive-select' }}>
                                    <MenuItem value="">Selecione uma opção</MenuItem>
                                    <MenuItem value="true">{currentLanguage.yes}</MenuItem>
                                    <MenuItem value="false">{currentLanguage.no}</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <div style={fieldLabelStyle}>{currentLanguage.active_tag}</div>
                            <FormControl fullWidth margin="dense">
                                <Select variant="outlined" displayEmpty value={filterEnabled} onChange={event => this.handleFilterChange({ filterEnabled: event.target.value })} inputProps={{ id: 'enabled-select' }}>
                                    <MenuItem value="">Selecione uma opção</MenuItem>
                                    <MenuItem value="true">{currentLanguage.yes}</MenuItem>
                                    <MenuItem value="false">{currentLanguage.no}</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>

                    {/* Última atualização */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 16 }}>
                        <div style={dateCardStyle}>
                            <div style={dateCardTitleStyle}>{currentLanguage.last_update}</div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ flex: '1 1 120px' }}>
                                    <div style={fieldLabelStyle}>{currentLanguage.from}</div>
                                    <FilterDatePicker disableFuture style={{ width: '100%' }} value={filterUpdateFrom} onChange={date => this.handleFilterChange({ filterUpdateFrom: date })} label="" />
                                </div>
                                <div style={{ flex: '1 1 120px' }}>
                                    <div style={fieldLabelStyle}>{currentLanguage.to}</div>
                                    <FilterDatePicker disableFuture style={{ width: '100%' }} value={filterUpdateUpTo} onChange={date => this.handleFilterChange({ filterUpdateUpTo: date })} label="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                        <PrimaryButton onClick={event => this.clearFilters(event)}>{currentLanguage.clear_fields}</PrimaryButton>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <PrimaryButton onClick={this.exportSelectedTags} disabled={exportingSelected}>
                                {exportingSelected ? 'Exportando...' : 'Exportar selecionadas'}
                            </PrimaryButton>
                            <PrimaryButton onClick={this.exportActiveTags} disabled={exporting}>
                                {exporting ? 'Exportando...' : 'Exportar Tags Ativas'}
                            </PrimaryButton>
                        </div>
                    </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardHead>
                        <TagIcon size={18} color="#1CA5D9" /> {currentLanguage.list} <CountBadge>{pageItems.length}</CountBadge>
                        {selectedCount > 0 && <CountBadge>{selectedCount} selecionada(s)</CountBadge>}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                            {listedItems.length > pageItems.length && selectedCount < listedItems.length && (
                                <Button size="small" color="primary" onClick={() => this.toggleSelected(listedItems, true)}>Selecionar todas ({listedItems.length})</Button>
                            )}
                            {selectedCount > 0 && <Button size="small" color="primary" onClick={this.clearSelection}>Limpar seleção</Button>}
                        </div>
                    </CardHead>
                    <OverflowXDiv>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <HeaderTableCell padding="checkbox">
                                    <Checkbox
                                        style={{ color: 'white' }}
                                        checked={pageItems.length > 0 && pageSelectedCount === pageItems.length}
                                        indeterminate={pageSelectedCount > 0 && pageSelectedCount < pageItems.length}
                                        disabled={pageItems.length === 0}
                                        onChange={event => this.toggleSelected(pageItems, event.target.checked)} />
                                </HeaderTableCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="equipmentSerialNumber">{currentLanguage.equipment_serial}</SortHeaderCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="equipmentExtraLabel">{currentLanguage.description}</SortHeaderCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="productName">{currentLanguage.product_name}</SortHeaderCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="productIsDvExclusive" align="center">{currentLanguage.product_exclusive}</SortHeaderCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="updateDate" align="center">{currentLanguage.last_update}</SortHeaderCell>
                                <SortHeaderCell sorter={collection.sorter} onSort={this.handleSort} field="enabled" align="center">{currentLanguage.active_tag}</SortHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageItems.map(tag => (
                                <TableRow key={tag.serialNumber} selected={this.isSelected(tag)}>
                                    <BodyTableCell padding="checkbox"><Checkbox color="primary" checked={this.isSelected(tag)} onChange={event => this.toggleSelected([tag], event.target.checked)} /></BodyTableCell>
                                    <BodyTableCell>{tag.serialNumber}</BodyTableCell>
                                    <BodyTableCell>{formatMainDescription(tag.equipmentExtraLabel, tag.equipmentExtraCode)}</BodyTableCell>
                                    <BodyTableCell>{tag.productName}</BodyTableCell>
                                    <BodyTableCell>{tag.productIsDvExclusive ? currentLanguage.yes : currentLanguage.no}</BodyTableCell>
                                    <BodyTableCell>{DateTimeService.formatDate(getAdjustedDate(tag.updateDate))}</BodyTableCell>
                                    <BodyTableCell><Checkbox color="primary" checked={tag.enabled} disabled={!allowTagToggle} onChange={event => this.toggleEnabled(tag, event.target.checked)} /></BodyTableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooterWithPaginator collection={collection} onCollectionUpdate={this.updateCollection} colSpan={7} />
                    </Table>
                </OverflowXDiv>
                </Card>
            </PageWrap>
        );
    }
}

export default withSnackbar(TagsPage);